import React, { ReactNode } from "react";

import { cn } from "@/lib/utils";

import {
  accountSettingsFieldDescriptionClassName,
  accountSettingsSettingCardClassName,
  accountSettingsSettingCardNoteClassName,
} from "../accountSettingsCardShell";

interface SettingCardProps {
  title: string;
  description?: string;
  children?: ReactNode;
  note?: ReactNode;
  rightContent?: ReactNode;
}

/** Vertical gap between stacked account-settings cards (matches broker management). */
export const accountSettingsCardStackClassName =
  "flex w-full min-w-0 flex-col gap-6";

/** Inline field + action row inside a setting card (select/input + button). */
export const accountSettingsFieldRowClassName =
  "flex flex-row flex-wrap items-center gap-3";

/** Standard vertical field stack inside account-settings cards (label → description → control). */
export const accountSettingsFieldClassName =
  "w-full min-w-0 gap-1.5 [&_[data-slot=field-description]+*]:mt-3.5 [&_[data-slot=field-label]+:not([data-slot=field-description])]:mt-3";

/** Label sits just above its control — no extra description line (e.g. Current email). */
export const accountSettingsFieldTightControlClassName =
  "w-full min-w-0 gap-1 data-account-settings-field-tight-control [&_[data-slot=field-label]+:not([data-slot=field-description])]:!mt-1.5 [&_[data-slot=field-description]+*]:!mt-3.5";

/** Gap between a setting card subtitle and its controls. */
export const accountSettingsCardBodyClassName = "w-full min-w-0";

export const accountSettingsCardBodyAfterDescriptionClassName = "mt-5";

/** Space between stacked controls inside a setting card body (e.g. switch row → select). */
export const accountSettingsFieldStackClassName = "flex flex-col gap-4";

/** Label + description pair inside a horizontal row (e.g. switch rows). */
export const accountSettingsFieldLabelStackClassName =
  "flex min-w-0 flex-1 flex-col gap-1.5";

/** Nested list row inside a setting card (authentication, devices, etc.). */
export const accountSettingsListRowClassName =
  "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 px-[22px] py-[22px]";

export const accountSettingsListRowMainClassName =
  "grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-3";

export const accountSettingsListRowAsideClassName =
  "flex shrink-0 items-center self-center";

export const accountSettingsListRowFieldClassName =
  "min-w-0 gap-0.5 [&_[data-slot=field-label]]:!mt-0 [&_[data-slot=field-description]]:!mt-0 [&_[data-slot=field-label]+[data-slot=field-description]]:!mt-0";

export const settingCardTitleClassName =
  "font-[family-name:var(--font-primary)] text-lg font-medium text-[#38BBD1]";

export const settingCardDescriptionClassName = accountSettingsFieldDescriptionClassName;

const SettingCard: React.FC<SettingCardProps> = ({
  title,
  description,
  children,
  note,
  rightContent,
}) => {
  const hasRightContent = !!rightContent;
  const hasChildContent = React.Children.toArray(children).length > 0;

  return (
    <div className="w-full min-w-0">
      <div
        data-setting-card=""
        className={cn(
          accountSettingsSettingCardClassName,
          note ? "rounded-b-none" : undefined
        )}
      >
        <div className="w-full max-w-full p-6">
          {hasRightContent ? (
            <div
              data-setting-card-header=""
              className="grid w-full grid-cols-1 items-center gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:gap-6"
            >
              <div
                data-setting-card-title-block=""
                className="flex min-w-0 flex-col justify-center gap-1.5"
              >
                <h3 className={cn(settingCardTitleClassName, "m-0")}>{title}</h3>
                {description ? (
                  <p className={cn(settingCardDescriptionClassName, "m-0")}>{description}</p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center justify-start md:justify-end">
                {rightContent}
              </div>
            </div>
          ) : (
            <div className="flex w-full flex-col">
              <h3
                className={cn(
                  settingCardTitleClassName,
                  "m-0",
                  description ? "mb-2" : "mb-4"
                )}
              >
                {title}
              </h3>
              {description ? (
                <p className={cn(settingCardDescriptionClassName, "m-0")}>{description}</p>
              ) : null}
              <div
                className={cn(
                  accountSettingsCardBodyClassName,
                  description && accountSettingsCardBodyAfterDescriptionClassName,
                  note && "mb-2"
                )}
              >
                {children}
              </div>
            </div>
          )}
          {hasRightContent && hasChildContent ? (
            <div className="mt-4 w-full min-w-0">{children}</div>
          ) : null}
        </div>
      </div>
      {note ? (
        <div className={accountSettingsSettingCardNoteClassName}>{note}</div>
      ) : null}
    </div>
  );
};

export default SettingCard;
